import { z } from "zod/v4";

import { AgenticLogicEvaluationTelemetryDescriptions } from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import { AgenticLogicDatasetRevisionStampSchema } from "../../../dtos/agentic-logic/datasets";
import { AgenticLogicAttemptRecordSchema } from "./attempts";
import {
  AgenticLogicEvaluationOutcomeSchema,
  AgenticLogicEvaluationSourceList,
  AgenticLogicExecutionTierSchema,
  AgenticLogicFallbackDispositionSchema,
} from "./vocab";

// ---------------------------------------------------------------------------
// Telemetry contract
// ---------------------------------------------------------------------------

export const AgenticLogicExclusionRemovalReasonList = [
  "product_id",
  "tag",
  "collection",
  "vendor",
  "metadata_unavailable",
] as const;

export const AgenticLogicExclusionRemovalReasonSchema = z.enum(
  AgenticLogicExclusionRemovalReasonList
);

export type AgenticLogicExclusionRemovalReason = z.infer<
  typeof AgenticLogicExclusionRemovalReasonSchema
>;

export const AgenticLogicExclusionRemovalSchema = z
  .object({
    itemId: z.string(),
    reason: AgenticLogicExclusionRemovalReasonSchema,
  })
  .strict();

export type AgenticLogicExclusionRemoval = z.infer<
  typeof AgenticLogicExclusionRemovalSchema
>;

export const AgenticLogicEvaluationTelemetrySchema = z
  .object({
    outcome: AgenticLogicEvaluationOutcomeSchema.describe(
      AgenticLogicEvaluationTelemetryDescriptions.outcome
    ),
    errorClass: z
      .string()
      .optional()
      .describe(AgenticLogicEvaluationTelemetryDescriptions.errorClass),
    // Populated from the SDK's resolved per-invocation dataset pins. The
    // impression store does not exist yet (M4) — this defines the field the
    // impression stamp will record; nothing persists it today.
    datasetRevisions: z
      .array(AgenticLogicDatasetRevisionStampSchema)
      .optional()
      .describe(AgenticLogicEvaluationTelemetryDescriptions.datasetRevisions),
    removedByExclusion: z
      .array(AgenticLogicExclusionRemovalSchema)
      .optional()
      .describe(AgenticLogicEvaluationTelemetryDescriptions.removedByExclusion),
    // Captured by the SDK runtime's accumulator, not by rule code — the
    // envelope is rule-authored and conformance-stripped, so attempts ride
    // the telemetry side channel instead. Absent means "no chain ran", never
    // "zero attempts".
    attempts: z
      .array(AgenticLogicAttemptRecordSchema)
      .optional()
      .describe(AgenticLogicEvaluationTelemetryDescriptions.attempts),
    // The evaluation-vs-impression flag: 'api' and 'preview' evaluations are
    // excluded from impression metrics and experiment readouts at the door.
    evaluationSource: z
      .enum(AgenticLogicEvaluationSourceList)
      .optional()
      .describe(AgenticLogicEvaluationTelemetryDescriptions.evaluationSource),
    // The four fields below are optional because the broker's early exits are
    // genuinely ignorant of them: an evaluation that never resolves an
    // assignment has no tier and no budget to report. Absent means "not known
    // at this exit", never "zero".
    executionTier: AgenticLogicExecutionTierSchema.optional().describe(
      AgenticLogicEvaluationTelemetryDescriptions.executionTier
    ),
    deadlineMs: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicEvaluationTelemetryDescriptions.deadlineMs),
    fallback: AgenticLogicFallbackDispositionSchema.optional().describe(
      AgenticLogicEvaluationTelemetryDescriptions.fallback
    ),
    classifierBreakerTripped: z
      .boolean()
      .optional()
      .describe(
        AgenticLogicEvaluationTelemetryDescriptions.classifierBreakerTripped
      ),
  })
  .describe(AgenticLogicEvaluationTelemetryDescriptions._schema);

export type AgenticLogicEvaluationTelemetry = z.infer<
  typeof AgenticLogicEvaluationTelemetrySchema
>;

assertDescriptions<
  typeof AgenticLogicEvaluationTelemetryDescriptions,
  AgenticLogicEvaluationTelemetry
>();
